import React from 'react';
import FlagIcon from '@atlaskit/icon/core/flag';
import FlagFilledIcon from '@atlaskit/icon/core/flag-filled';
import EmojiFlagsIcon from '@atlaskit/icon/glyph/emoji/flags';
import FlagFilledIconOld from '@atlaskit/icon/glyph/flag-filled';
import { colors } from '@atlaskit/theme';
import { token } from '@atlaskit/tokens';
import { fg } from '@atlassian/jira-feature-gating';
import { useIntl } from '@atlassian/jira-intl';
import { FIELD_TYPE_MAP } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/constants.tsx';
import { getUpdateAnalyticsFlowHelper } from '@atlassian/jira-issue-analytics/src/services/update-issue-field/index.tsx';
import { FLAGGED_CF_TYPE } from '@atlassian/jira-platform-field-config/src/index.tsx';
import { useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import {
	ADD,
	REMOVE,
} from '@atlassian/jira-software-add-comment-on-flag-action/src/ui/constants.tsx';
import {
	type MenuNode,
	MenuNodeType,
} from '@atlassian/jira-software-context-menu/src/common/types.tsx';
import { FlagWithCommentType } from '../../../../../../model/modals/types.tsx';
import { openModal } from '../../../../../../state/actions/modals/index.tsx';
import { useBacklogSelector, useBacklogActionCreator } from '../../../../../../state/index.tsx';
import {
	cardsForFlagWithCommentModalFromCardIdsSelector,
	getCards,
} from '../../../../../../state/selectors/cards/index.tsx';
import { canEditIssue } from '../../../../../../state/selectors/permissions/index.tsx';
import { FLAG_MENU_ID, FLAG_ADD_FLAG_MENU_ID, FLAG_REMOVE_FLAG_MENU_ID } from '../../constants.tsx';
import messages from '../../messages.tsx';
import { SelectedIssueFlaggedState, type Props, type ContentProps } from './types.tsx';

export const useGetSelectedIssuesFlaggedState = ({ cardIds }: Props): SelectedIssueFlaggedState => {
	const cards = useBacklogSelector(getCards);
	const flaggedStates = cardIds.map((cardId) => cards[cardId]?.isFlagged);

	if (flaggedStates.every((isFlagged) => isFlagged)) {
		return SelectedIssueFlaggedState.ALL_FLAGGED;
	}
	if (flaggedStates.every((isFlagged) => !isFlagged)) {
		return SelectedIssueFlaggedState.NONE_FLAGGED;
	}
	return SelectedIssueFlaggedState.INDETERMINATE;
};

export const useFlagContent = ({
	selectedIssuesFlaggedState,
	onToggleFlag,
	canFlag,
}: ContentProps): MenuNode[] => {
	const { formatMessage } = useIntl();

	if (!canFlag) {
		return [];
	}

	const addFlag: MenuNode = {
		type: MenuNodeType.Item,
		id: FLAG_ADD_FLAG_MENU_ID,
		label: formatMessage(messages.addFlag),
		onClick: () => onToggleFlag(true),
	};

	const removeFlag: MenuNode = {
		type: MenuNodeType.Item,
		id: FLAG_REMOVE_FLAG_MENU_ID,
		label: formatMessage(messages.removeFlag),
		onClick: () => onToggleFlag(false),
	};

	switch (selectedIssuesFlaggedState) {
		case SelectedIssueFlaggedState.NONE_FLAGGED:
			return [addFlag];
		case SelectedIssueFlaggedState.ALL_FLAGGED:
			return [removeFlag];
		case SelectedIssueFlaggedState.INDETERMINATE:
		default:
			return [
				{
					type: MenuNodeType.Item,
					label: formatMessage(messages.flag),
					id: FLAG_MENU_ID,
					children: [
						{
							...addFlag,
							icon: (
								<FlagFilledIcon
									label={formatMessage(messages.addFlag)}
									color={token('color.icon.danger', colors.R300)}
									LEGACY_fallbackIcon={FlagFilledIconOld}
									LEGACY_primaryColor={token('color.icon.danger', colors.R300)}
								/>
							),
						},
						{
							...removeFlag,
							icon: (
								<FlagIcon
									label={formatMessage(messages.removeFlag)}
									LEGACY_fallbackIcon={EmojiFlagsIcon}
									color={token('color.icon')}
								/>
							),
						},
					],
				},
			];
	}
};

export const useFlag = ({ cardIds }: Props) => {
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const selectedCardsForFlagWithCommentModal = useBacklogSelector((state) =>
		cardsForFlagWithCommentModalFromCardIdsSelector(state)(cardIds),
	);

	const onToggleFlag = useBacklogActionCreator((flag: boolean) => {
		if (cardIds.length === 1 && fg('one_event_rules_them_all_fg')) {
			getUpdateAnalyticsFlowHelper().fireAnalyticsStart(FIELD_TYPE_MAP[FLAGGED_CF_TYPE], {
				analytics: createAnalyticsEvent({}),
				attributes: {
					fieldType: FLAGGED_CF_TYPE,
					...(fg('one_event_fast_follow_fg') && {
						isContextMenuEditing: true,
					}),
				},
			});
		}
		return openModal({
			type: FlagWithCommentType,
			payload: {
				flagAction: flag ? ADD : REMOVE,
				cards: selectedCardsForFlagWithCommentModal,
			},
		});
	});

	const canFlag = useBacklogSelector(canEditIssue);
	const selectedIssuesFlaggedState = useGetSelectedIssuesFlaggedState({ cardIds });

	return useFlagContent({
		selectedIssuesFlaggedState,
		onToggleFlag,
		canFlag,
	});
};
